import { handleResponse, handleError, headerset } from "../apiUtils";
import { OrdineLavoro } from "components/main/produzione/ordiniLavoro/ordiniLavoroTypes";
import { FiltriOrdiniLavoro } from "components/userPreference/userPreferenceSlice";
import { convertToDateTime } from "components/common/dateUtils/convertToDateTime";

// ============================================
// BASE URL
// ============================================

const baseUrl = (ordineLavoroId?: number | null) => {
    let url: string = `${process.env.API_URL}/api/produzione/ordini-lavoro/`;

    if (ordineLavoroId) {
        url += `${ordineLavoroId}/`;
    }

    return url;
};

// ============================================
// QUERY STRING BUILDER
// ============================================

const buildQueryString = (filtri: FiltriOrdiniLavoro): string => {
    const params = new URLSearchParams();

    // Filtro stato (multiplo)
    if (filtri.stato && filtri.stato.length > 0) {
        params.append('stato', filtri.stato.join(','));
    }

    // Filtri data_arrivo_prevista (range)
    if (filtri.data_da) {
        params.append('data_da', filtri.data_da);
    }
    if (filtri.data_a) {
        params.append('data_a', filtri.data_a);
    }

    // Filtri tramite previsione (singoli)
    if (filtri.stagione_id) {
        params.append('stagione_id', filtri.stagione_id.toString());
    }
    if (filtri.mandatario_id) {
        params.append('mandatario_id', filtri.mandatario_id.toString());
    }
    if (filtri.confezionista_id) {
        params.append('confezionista_id', filtri.confezionista_id.toString());
    }
    if (filtri.commerciale_id) {
        params.append('commerciale_id', filtri.commerciale_id.toString());
    }

    // Filtro tecnica (multiplo)
    if (filtri.tecnica_id && filtri.tecnica_id.length > 0) {
        params.append('tecnica_id', filtri.tecnica_id.join(','));
    }

    // Filtro previsione diretta
    if (filtri.previsione_id) {
        params.append('previsione_id', filtri.previsione_id.toString());
    }

    // Ordinamento
    if (filtri.ordering) {
        params.append('ordering', filtri.ordering);
    }

    // Ricerca
    if (filtri.search) {
        params.append('search', filtri.search);
    }

    // Paginazione
    if (filtri.page) {
        params.append('page', filtri.page.toString());
    }
    if (filtri.numero_record) {
        params.append('numero_record', filtri.numero_record.toString());
    }

    return params.toString();
};

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Fetch lista paginata di ordini lavoro con filtri
 */
export function fetchOrdiniLavoro(filtri: FiltriOrdiniLavoro = {}) {
    let url = baseUrl();
    const queryString = buildQueryString(filtri);
    if (queryString) {
        url += `?${queryString}`;
    }

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get singolo ordine lavoro by ID
 */
export function getOrdineLavoro(ordineLavoroId: number) {
    const url = baseUrl(ordineLavoroId);

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Save (create/update) ordine lavoro
 */
export function saveOrdineLavoro(ordineLavoroToSave: OrdineLavoro, azione?: string) {
    let url = baseUrl(ordineLavoroToSave.id);

    // Aggiungi azione come query parameter se specificata
    if (azione) {
        url += `?azione=${azione}`;
    }

    // Prepara i dati per l'invio
    const ordineLavoroData = {
        ...ordineLavoroToSave,
        // Converti le date in formato ISO per il backend
        data_arrivo_prevista: ordineLavoroToSave.data_arrivo_prevista
            ? convertToDateTime(ordineLavoroToSave.data_arrivo_prevista)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        data_consegna_prevista: ordineLavoroToSave.data_consegna_prevista
            ? convertToDateTime(ordineLavoroToSave.data_consegna_prevista)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        data_ricevimento: ordineLavoroToSave.data_ricevimento
            ? convertToDateTime(ordineLavoroToSave.data_ricevimento)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        // Assicurati che le quantità siano numeri
        qta_prevista: ordineLavoroToSave.qta_prevista
            ? Number(ordineLavoroToSave.qta_prevista)
            : null,
        qta_arrivata: ordineLavoroToSave.qta_arrivata
            ? Number(ordineLavoroToSave.qta_arrivata)
            : null,
    };

    return fetch(url, {
        method: ordineLavoroToSave.id ? "PUT" : "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify(ordineLavoroData),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete ordine lavoro
 */
export function deleteOrdineLavoro(ordineLavoroToDelete: OrdineLavoro) {
    const url = baseUrl(ordineLavoroToDelete.id);

    return fetch(url, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}